import { assert, DefaultObservableValue, Notifier, Option, Terminable, Terminator } from "@opendaw/lib-std";
import { AudioBusBox } from "@opendaw/studio-boxes";
import { Pointers } from "@opendaw/studio-enums";
import { IconSymbol } from "../IconSymbol";
import { AudioBusBoxAdapter } from "./AudioBusBoxAdapter";
import { Devices } from "../DeviceAdapter";
export class AudioUnitInput {
    #terminator;
    #labelNotifier;
    #iconValue;
    #observable;
    #subscription = Terminable.Empty;
    constructor(pointerHub, boxAdapters) {
        this.#terminator = new Terminator();
        this.#labelNotifier = this.#terminator.own(new Notifier());
        this.#iconValue = this.#terminator.own(new DefaultObservableValue(IconSymbol.Unknown));
        this.#observable = this.#terminator.own(new DefaultObservableValue(Option.None));
        this.#terminator.own(this.#observable.subscribe(owner => {
            this.#subscription.terminate();
            this.#subscription = owner.getValue().match({
                none: () => {
                    this.#labelNotifier.notify(Option.None);
                    return Terminable.Empty;
                },
                some: ({ labelField, iconField }) => Terminable.many(iconField.catchupAndSubscribe(field => this.#iconValue.setValue(IconSymbol.fromName(field.getValue()))), labelField.catchupAndSubscribe(field => this.#labelNotifier.notify(Option.wrap(field.getValue()))))
            });
        }));
        this.#terminator.own(pointerHub.catchupAndSubscribe({
            onAdded: ({ box }) => {
                assert(this.#observable.getValue().isEmpty(), "Already set");
                const input = box instanceof AudioBusBox
                    ? boxAdapters.adapterFor(box, AudioBusBoxAdapter)
                    : boxAdapters.adapterFor(box, Devices.isInstrument);
                if (this.#observable.getValue().unwrapOrNull() !== input) {
                    this.#observable.setValue(Option.wrap(input));
                }
            },
            onRemoved: ({ box }) => {
                assert(this.#observable.getValue().unwrap("Cannot remove").box.address
                    .equals(box.address), "Unexpected value to remove");
                this.#observable.setValue(Option.None);
            }
        }, Pointers.InstrumentHost, Pointers.AudioOutput));
    }
    getValue() { return this.#observable.getValue(); }
    subscribe(observer) {
        return this.#observable.subscribe(observer);
    }
    catchupAndSubscribe(observer) {
        observer(this.#observable);
        return this.subscribe(observer);
    }
    catchupAndSubscribeLabelChange(observer) {
        observer(this.label);
        return this.#labelNotifier.subscribe(observer);
    }
    catchupAndSubscribeIconChange(observer) {
        return this.#iconValue.catchupAndSubscribe(observer);
    }
    set label(value) { this.getValue().ifSome(input => input.labelField.setValue(value)); }
    get label() { return this.getValue().map(input => input.labelField.getValue()); }
    set icon(value) { this.getValue().ifSome(input => input.iconField.setValue(IconSymbol.toName(value))); }
    get icon() {
        return this.getValue().match({
            none: () => IconSymbol.Unknown,
            some: input => IconSymbol.fromName(input.iconField.getValue())
        });
    }
    get iconValue() { return this.#iconValue; }
    terminate() {
        this.#terminator.terminate();
        this.#subscription.terminate();
    }
}
